import { ShowChart } from '@mui/icons-material';
import { Box, Paper, Skeleton } from '@mui/material';
import { defaults } from 'chart.js/auto';
import { ReactNode } from 'react';
import { Line } from 'react-chartjs-2';
import { withAlpha } from '../indicacao.utils';
import { EmptyState } from './EmptyState';
import { SectionHeader } from './SectionHeader';

defaults.maintainAspectRatio = false;
defaults.responsive = true;
defaults.plugins.title.display = false;

export interface ChartDataset {
	label: string;
	data: number[];
	color: string;
	fill?: boolean;
}

interface ChartCardProps {
	icon?: ReactNode;
	titulo: string;
	descricao: string;
	labels: string[];
	datasets: ChartDataset[];
	emptyText: string;
	formatY?: (value: number) => string;
	showLegend?: boolean;
	loading?: boolean;
}

export function ChartCard({
	icon,
	titulo,
	descricao,
	labels,
	datasets,
	emptyText,
	formatY,
	showLegend = false,
	loading,
}: ChartCardProps) {
	const temDados =
		labels.length > 0 &&
		datasets.some((dataset) => dataset.data.some((value) => Number(value) > 0));

	return (
		<Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid #f1f5f9', height: '100%' }}>
			<Box mb={2}>
				<SectionHeader icon={icon ?? <ShowChart />} titulo={titulo} descricao={descricao} />
			</Box>
			<Box height={280}>
				{loading ? (
					<Skeleton variant="rounded" width="100%" height="100%" sx={{ borderRadius: 2 }} />
				) : !temDados ? (
					<EmptyState icon={<ShowChart />} texto={emptyText} />
				) : (
					<Line
						data={{
							labels,
							datasets: datasets.map((dataset) => ({
								label: dataset.label,
								data: dataset.data,
								borderColor: dataset.color,
								backgroundColor: withAlpha(dataset.color, dataset.fill ? 0.15 : 0.08),
								fill: dataset.fill ?? false,
								tension: 0.35,
								pointRadius: 3,
								pointHoverRadius: 5,
								borderWidth: 2.5,
							})),
						}}
						options={{
							plugins: {
								legend: {
									display: showLegend,
									position: 'bottom',
									labels: { boxWidth: 10, usePointStyle: true, pointStyle: 'circle' },
								},
								tooltip: {
									backgroundColor: '#0f172a',
									padding: 10,
									titleFont: { size: 12 },
									bodyFont: { size: 12 },
								},
							},
							scales: {
								x: {
									grid: { display: false },
									ticks: { color: '#94a3b8', font: { size: 11 } },
									border: { display: false },
								},
								y: {
									beginAtZero: true,
									grid: { color: '#f1f5f9' },
									border: { display: false },
									ticks: {
										color: '#94a3b8',
										font: { size: 11 },
										precision: formatY ? undefined : 0,
										callback: formatY ? (value) => formatY(Number(value)) : undefined,
									},
								},
							},
						}}
					/>
				)}
			</Box>
		</Paper>
	);
}
